'use client'

import { useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { IconDiscount2 } from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { useSyneriseRecommendationView } from 'lib/synerise'
import {
  extractCustomerFromClientSession,
  extractTokenFromClientSession,
  getCustomerAgeStatus,
  useCustomerData
} from 'modules/customer'
import { DeliveryMethod } from 'modules/delivery'
import { SeoListName } from 'shared/analytics'
import Breadcrumbs from 'shared/ui/Breadcrumbs'
import Button from 'shared/ui/Button'
import Gallery from 'shared/ui/Gallery'
import { Modal, ModalDescription, ModalTitle } from 'shared/ui/Modal'
import { PATHS } from 'shared/utils/paths'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import { viewProduct } from '../../analytics'
import AddToFavouritesButton from '../../components/AddToFavouritesButton'
import AvailabilityCalendar from '../../components/AvailabilityCalendar/AvailabilityCalendar'
import Badge from '../../components/Badge'
import ProductSet from '../../components/ProductSet'
import ProductTags from '../../components/ProductTags'
import SwipeableSlider from '../../components/SwipeableSlider'
import { useProductListView } from '../../hooks/useProductsListView'
import { ProductActionButton, ProductSpecialPromotion, useCartCouponCode } from '../../ports'
import useQueryRecommendedSimilarItemProducts from '../../queries/useQueryRecommendedSimilarItemProducts'
import {
  MultipacksTypes,
  PageProductTypes,
  ProductBadgeProps,
  ProductFile,
  ProductTypes
} from '../../types/products'
import { badgesSegregation, tagsSegregation } from '../../utils/badges'
import {
  lastCategories,
  rootCategories,
  sortCategory
} from '../../utils/categories'
import {
  getMaxQuantityForSpecialOffer,
  getProductUnitDetails,
  isProductBlockedByCustomerAge
} from '../../utils/products'
import { formatPriceWithoutCurrency, getQuantityUnit } from '../../utils/quantity'

import ProductDescription from './components/ProductDescription'
import ProductDetailsAccordion from './components/ProductDetailsAccordion'
import ProductPrices from './components/ProductPrices'

import styles from './ProductPage.module.scss'

const PRODUCT_PAGE_LIST_NAME = SeoListName.PRODUCT_PAGE_LIST_NAME
const REPLACEMENTS_LIST_NAME = SeoListName.REPLACEMENTS_LIST_NAME
const SUGGESTED_PRODUCTS_LIST_NAME = SeoListName.SUGGESTED_PRODUCTS_LIST_NAME

interface ProductDetailsProps {
  product: PageProductTypes<DeliveryMethod>
  substitutes: ProductTypes[] | []
  slug: string
  distributionCenterId: string
}

const ProductPage = ({
  product,
  substitutes,
  distributionCenterId
}: ProductDetailsProps) => {
  const [quantityJump, setQuantityJump] = useState<number>(1)
  const session = useSession()
  const { user, token } = useMemo(() => {
    return {
      user: extractCustomerFromClientSession(session),
      token: extractTokenFromClientSession(session)
    }
  }, [session])
  // Reads the react-query customer cache, which DateOfBirthModal updates
  // immediately — the server-provided ageLimitMet may be stale until refetch.
  const { user: customer } = useCustomerData()

  const {
    name,
    nameLong,
    nameDescriptionShort,
    nameShort,
    files,
    ageLimitMet,
    productCategories,
    multipacks,
    tags,
    brand
  } = product

  const commercialTags = product?.commercialTags || []

  const [substitutesOpen, setSubstitutesOpen] = useState<boolean>(false)
  const parsedBadges: ProductBadgeProps[] = badgesSegregation(tags, 'full')
  const parsedTags: string[] = tagsSegregation([...tags, ...commercialTags])
  const isAvailable = product.isAvailable
  const pathname = usePathname()

  const cartCouponCode = useCartCouponCode()
  const [ref, inView] = useInView({ triggerOnce: true, initialInView: false })

  const { data } = useQueryRecommendedSimilarItemProducts({
    token: token || '',
    product,
    distributionCenterId
  })
  const recommendationProducts = useMemo(
    () => data?.recommendationProducts || [],
    [data?.recommendationProducts]
  )
  const recommendationMetadata = useMemo(
    () => data?.recommendationMetadata || undefined,
    [data?.recommendationMetadata]
  )

  const productCodes = useMemo(
    () =>
      recommendationProducts.map(
        recommendationProduct => recommendationProduct.code
      ),
    [recommendationProducts]
  )

  // Analytic events
  useEffect(() => {
    const fireAnalyticsEvent = () => {
      viewProduct({
        user,
        product,
        listName: PRODUCT_PAGE_LIST_NAME,
        ecommerce: {
          value: formatPriceWithoutCurrency(
            product?.specialOfferPricing?.priceGross?.value ||
              product?.pricing?.priceGross?.value ||
              0
          ),
          currency: 'PLN',
          coupon: cartCouponCode
        }
      })
    }

    if (product && session?.status !== 'loading') {
      fireAnalyticsEvent()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, session?.status])

  const shouldTriggerListView = useMemo(
    () =>
      product &&
      session?.status !== 'loading' &&
      inView &&
      isNotNilOrEmpty(recommendationProducts),
    [product, session?.status, inView, recommendationProducts]
  )

  useProductListView({
    shouldTrigger: shouldTriggerListView,
    user,
    products: recommendationProducts,
    listName: SUGGESTED_PRODUCTS_LIST_NAME
  })

  useSyneriseRecommendationView({
    recommendationMetadata,
    items: productCodes,
    shouldTrigger: shouldTriggerListView
  })

  const getMultipacks = (mp: MultipacksTypes[]) => {
    return mp.map(el => el.quantity)
  }

  const productSets = isNotNilOrEmpty(multipacks)
    ? getMultipacks(multipacks)
    : [1]

  const unitDetails = getProductUnitDetails(product)

  const categories = sortCategory(product)
  const lastCategory = lastCategories(categories)
  const rootCategory = rootCategories(product)

  const categoryAgeLimitMet = useMemo(() => {
    if (categories.length > 0) {
      const lastCategoryItem = categories[categories.length - 1]
      const found = productCategories?.find(
        cat => cat.id === lastCategoryItem.id
      )

      return found ? found?.ageLimitMet : false
    }

    return false
  }, [categories, productCategories])

  // Category flag is intentionally not passed: the previous rule reduced to
  // !ageLimitMet, and categoryAgeLimitMet falls back to false when unknown.
  const blurred = isProductBlockedByCustomerAge({
    product: { ageLimitMet, ageLimit: product.ageLimit },
    ageLimitMetCategory: undefined,
    userAgeStatus: getCustomerAgeStatus(customer?.dateOfBirth)
  })

  const availabilityPlan = product?.availabilityPlan || null

  const breadcrumbs = [
    { label: 'Wszystkie', path: PATHS.categories },
    ...categories.map(cat => ({
      path: cat?.totalSlug || '',
      label: cat?.name || ''
    })),
    { label: product.name, path: null }
  ]

  const images = isNotNilOrEmpty(files)
    ? files?.map((file: ProductFile) => {
        return {
          original: file.path,
          thumbnail: file.thumbnailPathSmall,
          originalTitle: name
        }
      })
    : [
        {
          original: '/static/photo-placeholder.svg',
          thumbnail: '/static/photo-placeholder.svg'
        }
      ]

  const handleSubstitutesOpen = () => setSubstitutesOpen(true)
  const handleSubstitutesClose = () => setSubstitutesOpen(false)

  const maxQuantityForSpecialOffer = getMaxQuantityForSpecialOffer(product)

  return (
    <>
      <Modal
        id='product-page-substitutes-modal'
        open={substitutesOpen}
        onClose={handleSubstitutesClose}
        size='large'
      >
        <ModalTitle visuallyHidden>Zamienniki produktu</ModalTitle>
        <ModalDescription>Wybierz zamienniki produktu</ModalDescription>
        <div className={styles.productCardWrapper}>
          <SwipeableSlider
            id='zamienniki'
            title='Zamienniki'
            products={substitutes}
            seoButtonPlace={pathname || ''}
            listName={REPLACEMENTS_LIST_NAME}
          />
        </div>
      </Modal>
      <div>
        <div>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </div>
        <div className={styles.productDetailsContentWrapper}>
          {blurred && (
            <div className={styles.blurredIconWrapper}>
              <Image
                src='/static/UnderEighteen.svg'
                alt='poniżej 18 lat'
                width={224}
                height={224}
              />
              <div className={styles.blurredIconDescription}>
                Produkt wyłącznie dla pełnoletnich
              </div>
            </div>
          )}
          <div
            className={clsx(styles.productDetailsContent, {
              [styles.blurred]: blurred
            })}
          >
            <div className={styles.galleryWrapper}>
              <div className={styles.favIconWrapper}>
                <AddToFavouritesButton
                  product={product}
                  idxOnList={undefined}
                  listName={PRODUCT_PAGE_LIST_NAME}
                  seoButtonPlace={pathname || ''}
                />
              </div>
              <Gallery images={images} />
              <div className={styles.webContent}>
                <ProductDescription
                  descriptionLong={product?.descriptionLong || ''}
                  descriptionShort={product?.descriptionShort || ''}
                />
              </div>
            </div>
            <div className={styles.detailsColumn}>
              <div className={styles.basicProductDetailsWrapper}>
                {isNotNilOrEmpty(parsedBadges) && (
                  <div className={styles.badges}>
                    {parsedBadges.map((badge, index) => (
                      <Badge
                        key={index}
                        badge={badge}
                        rootCategorySlug={rootCategory?.totalSlug || ''}
                      />
                    ))}
                  </div>
                )}
                <div>
                  {brand && brand.slug && brand.name && (
                    <Link
                      className={styles.productBrand}
                      href={`${PATHS.brand}/${typeof brand.slug === 'object' ? brand.slug.slug : brand.slug}`}
                    >
                      {brand.name}
                    </Link>
                  )}
                  <h1 className={styles.productName} {...addTestId('product-name')}>
                    {nameLong || nameDescriptionShort || name || nameShort}
                  </h1>
                  <span
                    className={styles.productCapacity}
                    {...addTestId('product-page-unit-details')}
                  >
                    {unitDetails}
                  </span>
                </div>
              </div>
              <div>
                {isNotNilOrEmpty(parsedTags) && (
                  <div className={styles.productLabelsWrapper}>
                    {parsedTags.map((tag, index) => (
                      <ProductTags
                        key={`product-label-${index}`}
                        name={tag}
                      />
                    ))}
                  </div>
                )}
                <ProductPrices product={product} quantityJump={quantityJump} />
              </div>
              {isNotNilOrEmpty(maxQuantityForSpecialOffer) &&
                product.showRemainingSpecialOfferQuantity && (
                  <div className={styles.maxPerSpecialOfferWrapper}>
                    <IconDiscount2 size={30} />
                    <p>
                      Maksymalna liczba dostępna w promocji:{' '}
                      {maxQuantityForSpecialOffer}
                    </p>
                  </div>
                )}

              {productSets.length > 1 && (
                <div className={styles.productSets}>
                  {productSets.map((jump: number) => (
                    <ProductSet
                      wider
                      key={`quantity-jump-${jump}`}
                      active={quantityJump === jump}
                      onClick={() => setQuantityJump(jump)}
                    >
                      {getQuantityUnit(jump)}
                    </ProductSet>
                  ))}
                </div>
              )}
              <div className={clsx(styles.buttonsWrapper, 'product-page-buttons')}>
                <div className={styles.buttonsFlexWrapper}>
                  <ProductActionButton
                    size='large'
                    product={product}
                    quantityJump={quantityJump}
                    seoButtonPlace={pathname || ''}
                    listName={PRODUCT_PAGE_LIST_NAME}
                    idxOnList={0}
                    icon={'/static/icons/cart-plus.svg'}
                    variant='primary'
                  />
                  <ProductSpecialPromotion product={product} source='product_page' />
                </div>
                {!isAvailable && isNotNilOrEmpty(substitutes) && (
                  <Button size='large' onClick={handleSubstitutesOpen}>
                    Pokaż zamienniki
                  </Button>
                )}
                {parsedTags.includes('Wyprzedaż') && (
                  <span className={styles.discountedDescriptionLabel}>
                    Produkt z wyprzedaży jest z krótką datą przydatności lub
                    jest z końcówki serii produktów sezonowych.
                  </span>
                )}
              </div>
              <div className={styles.mobileContent}>
                <ProductDescription
                  descriptionLong={product?.descriptionLong || ''}
                  descriptionShort={product?.descriptionShort || ''}
                />
              </div>
              <div className={styles.availabilityCalendarWrapper}>
                <div className={styles.availabilityCalendarTitle}>
                  Najbliższe terminy w dostawie
                </div>
                <AvailabilityCalendar
                  calendar={availabilityPlan}
                  deliveryMethods={product.deliveryMethods}
                />
              </div>
              <ProductDetailsAccordion product={product} />
            </div>
          </div>
        </div>
        {isNotNilOrEmpty(recommendationProducts) && (
          <div className={styles.sliderWrapper} ref={ref}>
            <SwipeableSlider
              id='inne-produkty-z-tej-kategorii'
              title='Podobne produkty'
              seoButtonPlace={pathname || ''}
              redirectButton={{
                label: 'Zobacz wszystkie',
                path: lastCategory?.totalSlug || PATHS.categories
              }}
              products={recommendationProducts.map((p: ProductTypes) => ({
                ...p,
                categoryAgeLimitMet
              }))}
              syneriseRecommendationMetadata={recommendationMetadata}
              listName={SUGGESTED_PRODUCTS_LIST_NAME}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default ProductPage
